import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useStudentAuth } from '../../../context/StudentAuthContext';
import { toast } from 'react-hot-toast';

const StudentQuizAttempts = () => {
  const { quizId } = useParams();
  const { studentApi } = useStudentAuth();
  const [attempts, setAttempts] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [quizId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [quizData, attemptsData] = await Promise.all([
        studentApi.get(`/student/quizzes/${quizId}`),
        studentApi.get(`/student/quizzes/${quizId}/attempts`)
      ]);
      setQuiz(quizData.data);
      setAttempts(attemptsData.data);
    } catch (error) {
      toast.error('Failed to load attempts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/student/quizzes" className="text-blue-600 hover:text-blue-800">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quiz Attempts</h1>
          <p className="text-gray-600">{quiz?.title}</p>
        </div>
      </div>

      {attempts.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center border border-gray-100">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No attempts yet</h3>
          <p className="text-gray-500 mb-4">You haven't taken this quiz yet.</p>
          <Link
            to={`/student/quiz/${quizId}`}
            className="inline-block px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Take Quiz
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {attempts.map((attempt) => (
            <div key={attempt.id} className="bg-white rounded-xl shadow border border-gray-100 p-6 hover:shadow-md transition">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-semibold text-gray-900">
                      Score: {attempt.score !== null ? `${attempt.score}` : 'Pending'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      attempt.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {attempt.status === 'completed' ? 'Completed' : 'In Progress'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 space-y-1">
                    <p>Started: {formatDate(attempt.start_time)}</p>
                    {attempt.end_time && <p>Ended: {formatDate(attempt.end_time)}</p>}
                    {attempt.time_spent > 0 && <p>Time spent: {formatTime(attempt.time_spent)}</p>}
                  </div>
                </div>
                {/* {attempt.status === 'completed' && (
                  <Link
                    to={`/student/quiz/${quizId}/attempt/${attempt.id}/review`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Review Answers
                  </Link>
                )} */}

                {attempt.status === 'completed' && attempt.can_review && (
  <Link
    to={`/student/quiz/${quizId}/attempt/${attempt.id}/review`}
    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
  >
    Review Answers
  </Link>
)}
                {attempt.status === 'in_progress' && (
                  <Link
                    to={`/student/quiz/${quizId}/take`}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
                  >
                    Continue Quiz
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentQuizAttempts;